import CloseIcon from "@mui/icons-material/Close";
import ErrorIcon from "@mui/icons-material/Error";
import ReportProblemIcon from "@mui/icons-material/ReportProblem";
import classNames from "classnames";
import type { MouseEvent } from "react";
import { useEffect, useMemo, useState } from "react";
import { useDispatch } from "react-redux";
import {
  setSelectedInteraction,
  setSelectedTakeSequence,
} from "../../app/slices/TrainingSlice";
import ErrorLogCard from "./error_log/ErrorLogCard";
import useErrorLogColumn from "./error_log/ErrorLogColumn.hook";
import LogData from "./error_log/LogData";
import "../advanced_search_column/AdvancedSearchOverlay.css";
import "./ValidationOverlay.css";

type LogFilter = "all" | "error" | "warning";

interface ValidationOverlayProps {
  isOpen: boolean;
  onClose: () => void;
}

export default function ValidationOverlay(props: ValidationOverlayProps) {
  const { isOpen, onClose } = props;
  const dispatch = useDispatch();
  const { getLogData } = useErrorLogColumn();
  const [filter, setFilter] = useState<LogFilter>("all");
  const [selectedKey, setSelectedKey] = useState<string>("");

  const logs = useMemo(() => getLogData(), [getLogData]);
  const errorCount = logs.filter((log) => log.logLevel === "error").length;
  const warningCount = logs.length - errorCount;

  const filteredLogs = useMemo(() => {
    if (filter === "all") return logs;
    return logs.filter((log) => log.logLevel === filter);
  }, [logs, filter]);

  useEffect(() => {
    if (!isOpen) return;

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") onClose();
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  const onLogClick = (key: string, log: LogData) => {
    setSelectedKey(key);
    if (log.opensTakeSequence && log.takeId) {
      dispatch(setSelectedTakeSequence(log.takeId));
    } else {
      dispatch(setSelectedInteraction(log.interactionId));
    }
    onClose();
  };

  const onBackdropClick = (event: MouseEvent<HTMLDivElement>) => {
    if (event.target === event.currentTarget) onClose();
  };

  return (
    <div
      aria-label="validation-overlay"
      className="advanced-search-overlay"
      onMouseDown={onBackdropClick}
    >
      <div
        className="advanced-search-overlay__panel"
        role="dialog"
        aria-modal="true"
        aria-label="Validation issues"
      >
        <div className="advanced-search-overlay__header">
          <div className="validation-overlay__title">
            <span>Validation</span>
            <span className="validation-overlay__title-count">
              {logs.length} {logs.length === 1 ? "issue" : "issues"}
            </span>
          </div>
          <button
            type="button"
            className="advanced-search-overlay__close"
            aria-label="Close validation overlay"
            onClick={onClose}
          >
            <CloseIcon fontSize="inherit" />
          </button>
        </div>

        <div className="advanced-search-overlay__filters">
          <button
            type="button"
            className={classNames("advanced-search-overlay__filter-chip", {
              "advanced-search-overlay__filter-chip--active": filter === "all",
            })}
            onClick={() => setFilter("all")}
          >
            <span>All</span>
            <span>{logs.length}</span>
          </button>
          <button
            type="button"
            className={classNames(
              "advanced-search-overlay__filter-chip",
              "validation-overlay__chip--error",
              {
                "advanced-search-overlay__filter-chip--active":
                  filter === "error",
              }
            )}
            onClick={() => setFilter("error")}
          >
            <ErrorIcon fontSize="inherit" />
            <span>Errors</span>
            <span>{errorCount}</span>
          </button>
          <button
            type="button"
            className={classNames(
              "advanced-search-overlay__filter-chip",
              "validation-overlay__chip--warning",
              {
                "advanced-search-overlay__filter-chip--active":
                  filter === "warning",
              }
            )}
            onClick={() => setFilter("warning")}
          >
            <ReportProblemIcon fontSize="inherit" />
            <span>Warnings</span>
            <span>{warningCount}</span>
          </button>
        </div>

        <div className="advanced-search-overlay__body">
          {filteredLogs.length === 0 ? (
            <div className="advanced-search-overlay__empty">
              No issues to show
            </div>
          ) : (
            <div className="advanced-search-overlay__results pit-creator-scrollbar validation-overlay__list">
              {filteredLogs.map((log) => {
                const key = log.interactionId + log.message;
                return (
                  <ErrorLogCard
                    key={key}
                    log={log}
                    isSelected={selectedKey === key}
                    onClick={() => onLogClick(key, log)}
                  />
                );
              })}
            </div>
          )}
        </div>

        <div className="advanced-search-overlay__footer">
          <span>
            <kbd>Enter</kbd> jump
          </span>
          <span>
            <kbd>Esc</kbd> close
          </span>
        </div>
      </div>
    </div>
  );
}
